// src/pages/league-dashboard/live-draft/utils/performanceLogger.js
// Performance logging utility for draft components

// Enable/disable logging via environment or localStorage
const isEnabled = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('DRAFT_PERF_LOG') === 'true' || process.env.NODE_ENV === 'development';
};

// Store render counts
const renderCounts = new Map();
const operationTimings = [];

/**
 * Log component render
 * @param {string} componentName - Name of the component
 * @param {Object} props - Component props (optional, for debugging)
 */
export const logRender = (componentName, props = null) => {
  if (!isEnabled()) return;

  const count = (renderCounts.get(componentName) || 0) + 1;
  renderCounts.set(componentName, count);

  console.log(
    `%c[RENDER] ${componentName} #${count}`,
    'color: #4CAF50; font-weight: bold;',
    props ? { propsKeys: Object.keys(props) } : ''
  );
};

/**
 * Start timing an operation
 * @param {string} operationName - Name of the operation
 * @returns {Function} - Call to end timing and log result
 */
export const startTiming = (operationName) => {
  if (!isEnabled()) return () => {};

  const startTime = performance.now();

  return (details = {}) => {
    const duration = performance.now() - startTime;
    const timing = {
      operation: operationName,
      duration: duration.toFixed(2),
      timestamp: new Date().toISOString(),
      ...details
    };

    operationTimings.push(timing);

    // Color based on duration
    const color = duration < 16 ? '#4CAF50' : duration < 50 ? '#FFC107' : '#F44336';
    console.log(
      `%c[TIMING] ${operationName}: ${duration.toFixed(2)}ms`,
      `color: ${color}; font-weight: bold;`,
      details
    );

    return duration;
  };
};

/**
 * Log socket event
 * @param {string} eventName - Name of the socket event
 * @param {Object} data - Event data
 */
export const logSocketEvent = (eventName, data = null) => {
  if (!isEnabled()) return;

  console.log(
    `%c[SOCKET] ${eventName}`,
    'color: #2196F3; font-weight: bold;',
    data ? { dataKeys: Object.keys(data), data } : ''
  );
};

/**
 * Log state update
 * @param {string} hookName - Name of the hook
 * @param {string} stateName - Name of the state being updated
 * @param {any} newValue - New value (or summary)
 */
export const logStateUpdate = (hookName, stateName, newValue = null) => {
  if (!isEnabled()) return;

  console.log(
    `%c[STATE] ${hookName}.${stateName}`,
    'color: #9C27B0; font-weight: bold;',
    newValue !== null ? { value: newValue } : ''
  );
};

/**
 * Get performance report
 * @returns {Object} - Report with render counts and timings
 */
export const getPerformanceReport = () => {
  return {
    renderCounts: Object.fromEntries(renderCounts),
    operationTimings: operationTimings.slice(-50), // Last 50 operations
    summary: {
      totalRenders: Array.from(renderCounts.values()).reduce((a, b) => a + b, 0),
      totalOperations: operationTimings.length,
      slowOperations: operationTimings.filter(t => parseFloat(t.duration) > 50).length
    }
  };
};

/**
 * Reset performance data
 */
export const resetPerformanceData = () => {
  renderCounts.clear();
  operationTimings.length = 0;
  console.log('%c[PERF] Performance data reset', 'color: #607D8B;');
};

/**
 * Enable/disable performance logging
 * @param {boolean} enabled
 */
export const setPerformanceLogging = (enabled) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('DRAFT_PERF_LOG', enabled ? 'true' : 'false');
    console.log(`%c[PERF] Logging ${enabled ? 'enabled' : 'disabled'}`, 'color: #607D8B;');
  }
};

// Expose to window for debugging
if (typeof window !== 'undefined') {
  window.draftPerf = {
    getReport: getPerformanceReport,
    reset: resetPerformanceData,
    enable: () => setPerformanceLogging(true),
    disable: () => setPerformanceLogging(false)
  };
}

export default {
  logRender,
  startTiming,
  logSocketEvent,
  logStateUpdate,
  getPerformanceReport,
  resetPerformanceData,
  setPerformanceLogging
};
